import * as React from "react";
import { Link, useParams, useSearchParams } from "react-router";

import { useAuth, useNavigation } from "@/providers";
import {
  FormPageHeading,
  PageBreadcrumbs,
  PageLayout,
} from "@/shared/components";
import { type AgentRecord } from "@/shared/domain/agents";
import { useAgent, useLaunchAgent } from "@/shared/domain/agents/hooks";
import { Spinner } from "@/shared/foundation/primitives";
import { Goto } from "@/shared/navigation";
import { useRegisterViewingContext } from "@/shared/services/ViewingContextService";

import styles from "./LaunchAgent.module.css";
import { LaunchAgentForm } from "./LaunchAgentForm";

/**
 * Launch one stored agent: fill in what it asks for, say who may read the
 * thread, and land on the thread it starts.
 *
 * The form only mounts once the agent has landed: its variables are the
 * form's fields. The route's loader has usually put the agent in the cache
 * already, so the spinner is the cold-path fallback.
 */
export function LaunchAgent() {
  const { agentId } = useParams<{ agentId: string }>();
  const [searchParams] = useSearchParams();
  const datasetIdFromUrl = searchParams.get("dataset_id") ?? "";
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.org_id;
  const agentQuery = useAgent(agentId, { orgId });
  useRegisterViewingContext("agent", { orgId: agentQuery.data?.org_id });

  const name = agentQuery.data?.name ?? "agent";

  // The spinner is gated on `data === undefined`, not `isLoading`: `useAgent`
  // is disabled until `orgId` resolves, and a disabled query reports
  // `isLoading: false`.
  return (
    <PageLayout
      title={`Roboto - Launch ${name}`}
      headerContentLeft={
        <PageBreadcrumbs>
          <Link to={Goto.route.Agents}>Agents</Link>
          {agentId !== undefined && (
            <Link to={Goto.createAgentRoute(agentId)}>
              {agentQuery.data?.name ?? "Agent"}
            </Link>
          )}
          <span>Launch</span>
        </PageBreadcrumbs>
      }
    >
      <div className={styles.page} data-testid="launch-agent-page">
        <FormPageHeading
          title={`Launch ${name}`}
          subtitle={"Starts a new thread."}
        />
        {agentQuery.error !== null && (
          <p
            className={styles.notice}
            data-testid="launch-agent-error"
            role="alert"
          >
            {agentQuery.error.message}
          </p>
        )}
        {agentQuery.error === null && agentQuery.data === undefined && (
          <Spinner data-testid="launch-agent-loading" />
        )}
        {agentQuery.data !== undefined && (
          <Launcher
            agent={agentQuery.data}
            orgId={orgId}
            datasetIdFromUrl={datasetIdFromUrl}
          />
        )}
      </div>
    </PageLayout>
  );
}

interface LauncherProps {
  agent: AgentRecord;
  /** Undefined until an org resolves; a launch before then is refused. */
  orgId?: string;
  datasetIdFromUrl: string;
}

function Launcher({ agent, orgId, datasetIdFromUrl }: LauncherProps) {
  const { goto } = useNavigation();
  const { mutateAsync: launch } = useLaunchAgent();
  const backToAgent = React.useCallback(() => {
    goto.agent(agent.agent_id);
  }, [goto, agent.agent_id]);

  return (
    <LaunchAgentForm
      agent={agent}
      datasetIdFromUrl={datasetIdFromUrl}
      onSubmit={async (request) => {
        if (orgId === undefined) {
          throw new Error(
            "No organization is selected, so this agent cannot be launched.",
          );
        }
        const thread = await launch({
          orgId,
          agentId: agent.agent_id,
          request,
        });
        goto.agentThread({ threadId: thread.thread_id });
      }}
      onCancel={backToAgent}
    />
  );
}
